import React, {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	fallbackContentStateInfoPopupCardPlacement,
	getContentStateInfoPopupCardPlacement,
	type ContentStateInfoPopupCardPlacement,
} from './contentStateInfoPopupCardPlacement.ts';

type ContentStateInfoPopupCardPlacementContextValue = ContentStateInfoPopupCardPlacement & {
	setPopupElement: (element: HTMLDivElement | null) => void;
	setTriggerElement: (element: HTMLDivElement | null) => void;
};

const ContentStateInfoPopupCardPlacementContext =
	createContext<ContentStateInfoPopupCardPlacementContextValue | null>(null);

const placementsAreEqual = (
	first: ContentStateInfoPopupCardPlacement,
	second: ContentStateInfoPopupCardPlacement,
) =>
	first.position === second.position &&
	first.offset[0] === second.offset[0] &&
	first.offset[1] === second.offset[1];

export const useContentStateInfoPopupCardPlacement =
	(): ContentStateInfoPopupCardPlacementContextValue | null =>
		useContext(ContentStateInfoPopupCardPlacementContext);

export const ContentStateInfoPopupCardPlacementProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const popupElementRef = useRef<HTMLDivElement | null>(null);
	const triggerElementRef = useRef<HTMLDivElement | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const isMeasurementActiveRef = useRef(false);
	const [placement, setPlacement] = useState<ContentStateInfoPopupCardPlacement>(
		fallbackContentStateInfoPopupCardPlacement,
	);

	const measure = useCallback(() => {
		const popupElement = popupElementRef.current;
		const triggerElement = triggerElementRef.current;
		const nextPlacement = getContentStateInfoPopupCardPlacement({
			popupRect: popupElement?.getBoundingClientRect() ?? null,
			triggerRect: triggerElement?.getBoundingClientRect() ?? null,
			viewportWidth: window.innerWidth,
		});

		setPlacement((currentPlacement) =>
			placementsAreEqual(currentPlacement, nextPlacement) ? currentPlacement : nextPlacement,
		);
	}, []);

	const scheduleMeasure = useCallback(() => {
		if (animationFrameRef.current !== null) {
			return;
		}
		if (typeof window.requestAnimationFrame !== 'function') {
			measure();
			return;
		}

		animationFrameRef.current = window.requestAnimationFrame(() => {
			animationFrameRef.current = null;
			measure();
		});
	}, [measure]);

	const setObservedElement = useCallback(
		(elementRef: React.MutableRefObject<HTMLDivElement | null>, element: HTMLDivElement | null) => {
			if (elementRef.current === element) {
				return;
			}

			if (elementRef.current) {
				resizeObserverRef.current?.unobserve(elementRef.current);
			}
			elementRef.current = element;
			if (!isMeasurementActiveRef.current) {
				return;
			}
			if (element) {
				resizeObserverRef.current?.observe(element);
			}
			scheduleMeasure();
		},
		[scheduleMeasure],
	);

	const setPopupElement = useCallback(
		(element: HTMLDivElement | null) => setObservedElement(popupElementRef, element),
		[setObservedElement],
	);
	const setTriggerElement = useCallback(
		(element: HTMLDivElement | null) => setObservedElement(triggerElementRef, element),
		[setObservedElement],
	);

	useLayoutEffect(() => {
		isMeasurementActiveRef.current = true;

		if (typeof ResizeObserver !== 'undefined') {
			resizeObserverRef.current = new ResizeObserver(measure);
			if (popupElementRef.current) resizeObserverRef.current.observe(popupElementRef.current);
			if (triggerElementRef.current) resizeObserverRef.current.observe(triggerElementRef.current);
		}
		measure();

		if (typeof window.addEventListener === 'function') {
			window.addEventListener('resize', scheduleMeasure);
			window.addEventListener('scroll', scheduleMeasure, true);
		}

		return () => {
			isMeasurementActiveRef.current = false;
			resizeObserverRef.current?.disconnect();
			resizeObserverRef.current = null;
			if (animationFrameRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
				window.cancelAnimationFrame(animationFrameRef.current);
			}
			animationFrameRef.current = null;
			if (typeof window.removeEventListener === 'function') {
				window.removeEventListener('resize', scheduleMeasure);
				window.removeEventListener('scroll', scheduleMeasure, true);
			}
		};
	}, [measure, scheduleMeasure]);

	const contextValue = useMemo(
		() => ({ ...placement, setPopupElement, setTriggerElement }),
		[placement, setPopupElement, setTriggerElement],
	);

	return (
		<ContentStateInfoPopupCardPlacementContext.Provider value={contextValue}>
			{children}
		</ContentStateInfoPopupCardPlacementContext.Provider>
	);
};
